import { nueQuaternionToWebXR, nueToWebXR } from "gps-plus-slam-js";
import { createSelector } from "@reduxjs/toolkit";
//#region ../src/state/app-selectors.ts
/**
* App-level memoized selectors for CombinedRootState.
*
* Wraps library getter functions with createSelector (RTK/reselect) for
* standard memoization. The library getters already return stable references
* via immutable state, but createSelector adds:
* - Explicit memoization contract (input comparison → cached output)
* - Consistent select* naming convention matching selectCachedKnownRefPoints
* - Composition-ready building blocks for derived selectors
*
* Used by subscribeToSelector in store-subscribers.ts for change detection.
*
* @see gps-plus-slam/GpsPlusSlamJs_Docs/docs/2026-04-07-architecture-observations-consolidated.md §1
*/
const EMPTY_GPS_POSITIONS = [];
const EMPTY_ODOM_POSITIONS = [];
const EMPTY_ODOM_ROTATIONS = [];
const EMPTY_FRAME_TILES = [];
const selectGpsData = (state) => state.gpsData;
/** Alignment matrix (4×4), or null if not yet computed. */
const selectAlignmentMatrix = createSelector([selectGpsData], (gpsData) => gpsData?.gpsEvents?.alignmentMatrix ?? null);
/** Recorded GPS positions with metadata. */
const selectGpsPositions = createSelector([selectGpsData], (gpsData) => gpsData?.gpsEvents?.gpsPositions ?? EMPTY_GPS_POSITIONS);
/** Recorded odometry positions (AR-local space). */
const selectOdometryPositions = createSelector([selectGpsData], (gpsData) => gpsData?.gpsEvents?.odometryPositions ?? EMPTY_ODOM_POSITIONS);
/** Recorded odometry rotations (AR-local space). */
const selectOdometryRotations = createSelector([selectGpsData], (gpsData) => gpsData?.gpsEvents?.odometryRotations ?? EMPTY_ODOM_ROTATIONS);
/** GPS zero reference (origin for coordinate conversion), or null. */
const selectZeroReference = createSelector([selectGpsData], (gpsData) => gpsData?.zero ?? null);
/** User-defined reference points for ground truth validation. */
/**
* Captured AR image frames in WebXR coordinate space.
*
* Reads `state.gpsData.odometryPath.points` (which the library reducer
* stores in NUE convention — see `gpsDataSlice.ts` `add2dImage`) and
* converts each entry back to WebXR so the live AR scene visualizer can
* apply the pose directly. This is the source of truth for the
* frame-tile visualizer; the legacy `framesInScene` slice is a dead
* mirror scheduled for removal (Step 5 of the 2026-05-27
* slice-collapse plan).
*
* Memoized via createSelector keyed on `odometryPath.points` (NOT the whole
* `gpsData`). The library reducer uses Immer, so unrelated updates (GPS
* observations, VIO offsets) produce a new `gpsData` reference while
* `odometryPath.points` keeps its reference via structural sharing. Keying on
* the points array therefore preserves referential stability of the output
* across those dispatches — subscribers like `wireFrameTileSubscribers` only
* re-run when frames actually change.
*/
const selectOdometryPathPoints = (state) => state.gpsData?.odometryPath?.points;
const selectFrameTilesInWebXR = createSelector([selectOdometryPathPoints], (points) => {
	if (!points || points.length === 0) return EMPTY_FRAME_TILES;
	return points.map((p) => ({
		imageFile: p.imageFile,
		position: nueToWebXR(p.position),
		rotation: nueQuaternionToWebXR(p.rotation),
		screenRotation: p.screenRotation,
		capturedAt: p.capturedAt,
		width: p.width,
		height: p.height
	}));
});
//#endregion
export { selectAlignmentMatrix, selectFrameTilesInWebXR, selectGpsPositions, selectOdometryPositions, selectOdometryRotations, selectZeroReference };
